<template>
  <div :class="['page', $style.downTransPage]">
    <div :class="$style.pageSections">
      <div :class="$style.downTransBox">
        <div :class="$style.downTransBoxtext">
          You can download game transcripts or message transcripts with optional date filters.
        </div>

        <div :class="$style.defaultNote">
          Default range is last 90 days.
        </div>

        <div v-if="!isAuthorized" :class="$style.passwordSection">
          <input
            v-model="password"
            type="password"
            placeholder="Enter access password"
            :class="$style.passwordInput"
            @keyup.enter="verifyPassword"
          />

          <div :class="$style.downTransBoxCta">
            <UtilButton
              text="Unlock Downloads"
              :clickFunction="verifyPassword"
              backBg="#1F2937"
              frontBg="#4B5563"
              textColor="#FFFFFF"
              :button-width="'100%'"
            />
          </div>
        </div>

        <div v-else :class="$style.downTransBoxCtaContainer">
          <div :class="$style.sectionBox">
            <h3>Game Transcripts</h3>

            <div :class="$style.dateSection">
              <div>
                <label>Start Date</label>
                <input type="date" v-model="transStartDate" />
              </div>

              <div>
                <label>End Date</label>
                <input type="date" v-model="transEndDate" />
              </div>
            </div>

            <div :class="$style.downTransBoxCta">
              <UtilButton
                text="Download Game Transcripts"
                :clickFunction="downloadTranscripts"
                backBg="#1F2937"
                frontBg="#4B5563"
                textColor="#FFFFFF"
                :button-width="'100%'"
              />
            </div>
          </div>

          <div :class="$style.sectionBox">
            <h3>Message Transcripts</h3>

            <div :class="$style.dateSection">
              <div>
                <label>Start Date</label>
                <input type="date" v-model="msgStartDate" />
              </div>

              <div>
                <label>End Date</label>
                <input type="date" v-model="msgEndDate" />
              </div>
            </div>

            <div :class="$style.downTransBoxCta">
              <UtilButton
                text="Download Message Transcripts"
                :clickFunction="downloadMessages"
                backBg="#1F2937"
                frontBg="#4B5563"
                textColor="#FFFFFF"
                :button-width="'100%'"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="loader-container">
      <img :src="hostLoaderGif" alt="loader" class="loader-gif" />
    </div>
  </div>
</template>

<script>
import UtilButton from "@/components/util/UtilButton.vue";
import styles from "@/css/DownloadTranscriptsView.module.css";
import globalStyles from "@/css/global.module.css";
import gameService from "@/services/game.service";
import HostLoaderGif from "@/assets/gifs/host-loader.svg";
import { toast } from "vue3-toastify";

export default {
  name: "DownloadTranscriptsView",
  components: { UtilButton },

  data() {
    return {
      hostLoaderGif: HostLoaderGif,
      isLoading: false,
      password: "",
      isAuthorized: false,
      transcriptsToken: null,

      msgStartDate: "",
      msgEndDate: "",
      transStartDate: "",
      transEndDate: "",
    };
  },

  computed: {
    $style() {
      return styles;
    },
    $globalStyle() {
      return globalStyles;
    },
  },

  mounted() {
    const format = (d) => {
      return (
        d.getFullYear() +
        "-" +
        String(d.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(d.getDate()).padStart(2, "0")
      );
    };

    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - 90);

    this.msgStartDate = format(startDate);
    this.msgEndDate = format(today);

    this.transStartDate = format(startDate);
    this.transEndDate = format(today);
  },

  methods: {
    async verifyPassword() {
      this.isLoading = true;

      try {
        const response = await gameService.verifyTranscriptsPassword(this.password);
        this.transcriptsToken = response.token;
        this.isAuthorized = true;
        this.password = "";
        toast("Access granted", { type: "success" });
      } catch (error) {
        console.error(error);
        toast("Invalid password", { type: "error" });
      } finally {
        this.isLoading = false;
      }
    },

    async downloadTranscripts() {
      if (
        this.transStartDate &&
        this.transEndDate &&
        this.transStartDate > this.transEndDate
      ) {
        toast("Start date cannot be after end date", { type: "error" });
        return;
      }

      this.isLoading = true;

      try {
        const result = await gameService.allTranscriptsDownload2(
          {
            start_date: this.transStartDate || null,
            end_date: this.transEndDate || null,
          },
          this.transcriptsToken
        );

        if (result && result.success === false) {
          toast(result.message || "No game transcripts found", { type: "info" });
          return;
        }

        toast("Game transcripts downloaded successfully", { type: "success" });
      } catch (error) {
        console.error(error);
        toast("Failed to download game transcripts", { type: "error" });
      } finally {
        this.isLoading = false;
      }
    },

    async downloadMessages() {
      if (
        this.msgStartDate &&
        this.msgEndDate &&
        this.msgStartDate > this.msgEndDate
      ) {
        toast("Start date cannot be after end date", { type: "error" });
        return;
      }

      this.isLoading = true;

      try {
        const result = await gameService.allMessagesDownload2(
          {
            start_date: this.msgStartDate || null,
            end_date: this.msgEndDate || null,
          },
          this.transcriptsToken
        );

        if (result && result.success === false) {
          toast(result.message || "No message transcripts found", { type: "info" });
          return;
        }

        toast("Message transcripts downloaded successfully", { type: "success" });
      } catch (error) {
        console.error(error);
        toast("Failed to download message transcripts", { type: "error" });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>